import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import React, { useCallback, useMemo, useState } from "react"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { MenuItem, Stack, Typography } from "@mui/material"
import { useNavigate } from "react-router"
import { fetchResources, FetchResourcesResponse, ServiceCenterResourcesQuery } from "../../resourcesApi"
import { useApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../../context/contextSelectors"
import { OrganizationResourceUnion, ResourceType } from "../../resourcesTypes"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faImage } from "@fortawesome/pro-light-svg-icons"
import { getEditServiceCenterResourceRoomRoute } from "../../../../../../../constants/routes"
import { DeleteConfirmDialog } from "../../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { PreviewProductImage } from "../../../../../../merchant/admin/products/components/tableElements/PreviewProductImage"
import { DestructiveMenuItem } from "../../../../../../../shared/components/menus/DestructiveMenuItem"

const transformData = (data: FetchResourcesResponse) => {
    return data.resources
}

type DefaultPreviewProductImageStateType = Readonly<{ show: boolean; image: string }>
const defaultPreviewProductImageState: DefaultPreviewProductImageStateType = { show: false, image: "" }

export function ServiceCenterAdminResourcesRoom() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)
    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>({
        showConfirmModal: false,
        confirmModalText: "",
        itemId: null,
    })
    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )

    const rooms = useApiQuery(fetchResources, {
        queryName: ServiceCenterResourcesQuery.SERVICE_CENTER_RESOURCES,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            type: ResourceType.ROOM,
        },
        enabled: Boolean(serviceCenterId),
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(rooms)

    const columns = useMemo<MRT_ColumnDef<OrganizationResourceUnion>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("service-center-admin-organization-resources-table-room-header-id"),
                accessorKey: "reference",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("service-center-admin-organization-resources-table-header-name"),
                accessorKey: "name",
                Cell: ({ row }) => {
                    return (
                        <Stack direction="row" alignItems="center" spacing=".5rem">
                            <Typography>{row.original.name}</Typography>
                            {row.original?.image?.url && (
                                <FontAwesomeIcon
                                    icon={faImage}
                                    size="lg"
                                    onClick={(imageUrl) => {
                                        setPreviewImage({ show: true, image: row.original?.image?.url })
                                    }}
                                />
                            )}
                        </Stack>
                    )
                },
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    accessorKey: "status",
                    header: l10n.getString("service-center-admin-organization-resources-table-header-status"),
                },
                (row) => row.status === "Active",
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<OrganizationResourceUnion>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    navigate(getEditServiceCenterResourceRoomRoute(row.original.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-organization-resources-table-edit-item">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <DestructiveMenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    setConfirmModalState({
                        showConfirmModal: true,
                        confirmModalText: row.original.name,
                        itemId: row.original.id,
                    })
                    closeMenu()
                }}
            >
                <LocalizedStrict id="service-center-admin-organization-resources-table-delete-item">
                    <span>Delete</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
        ],
        [],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}

            {confirmModalState.showConfirmModal && (
                <DeleteConfirmDialog
                    typeOfEntity={l10n.getString(
                        "service-center-admin-organization-resources-table-room-delete-modal-title",
                    )}
                    entityName={confirmModalState.confirmModalText}
                    handleClick={() => {}} //todo
                    onClose={() =>
                        setConfirmModalState({
                            showConfirmModal: false,
                            confirmModalText: "",
                            itemId: null,
                        })
                    }
                    open={confirmModalState.showConfirmModal}
                />
            )}
        </>
    )
}
